import { nextTick, ref, type Ref } from 'vue';
import type { WorkspaceSummary } from '../types/workspace';

export function useWorkspacePicker(options: {
  workspace: Ref<WorkspaceSummary | null>;
  availableWorkspaces: Ref<WorkspaceSummary[]>;
  archivedWorkspaces: Ref<WorkspaceSummary[]>;
  loadWorkspaceList: () => Promise<unknown>;
  restoreWorkspace: (id: string) => Promise<unknown>;
  switchWorkspace: (id: string) => Promise<unknown>;
  createWorkspace: (name: string) => Promise<WorkspaceSummary>;
  initializeWorkspace: (id: string) => Promise<unknown>;
  beforeSwitch: () => Promise<void>;
  afterSwitch: () => void;
  navigateToWorkspace: (id: string) => void;
}) {
  const workspacePickerOpen = ref(false);
  const managedWorkspace = ref<WorkspaceSummary | null>(null);
  const workspaceSwitching = ref(false);
  const workspacePickerError = ref('');
  const newWorkspaceName = ref('');

  async function toggleWorkspacePicker() {
    workspacePickerError.value = '';
    if (workspacePickerOpen.value) { workspacePickerOpen.value = false; return; }
    workspacePickerOpen.value = true;
    try { await options.loadWorkspaceList(); }
    catch (cause) { workspacePickerError.value = cause instanceof Error ? cause.message : 'Welten konnten nicht geladen werden.'; }
  }

  function openWorkspaceManagement(item: WorkspaceSummary) {
    if (item.role !== 'owner') return;
    managedWorkspace.value = item;
    workspacePickerOpen.value = false;
  }

  async function workspaceManagementChanged() {
    await nextTick();
    if (!managedWorkspace.value) return;
    managedWorkspace.value = [...options.availableWorkspaces.value, ...options.archivedWorkspaces.value].find((item) => item.id === managedWorkspace.value?.id)
      ?? (options.workspace.value?.id === managedWorkspace.value.id ? options.workspace.value : null);
  }

  async function restoreArchivedWorkspace(item: WorkspaceSummary) {
    workspacePickerError.value = '';
    workspaceSwitching.value = true;
    try { await options.restoreWorkspace(item.id); }
    catch (cause) { workspacePickerError.value = cause instanceof Error ? cause.message : 'Die Welt konnte nicht wiederhergestellt werden.'; }
    finally { workspaceSwitching.value = false; }
  }

  async function switchWorkspace(workspaceId: string) {
    if (!options.workspace.value || workspaceId === options.workspace.value.id || workspaceSwitching.value) {
      workspacePickerOpen.value = false;
      return;
    }
    workspacePickerError.value = '';
    workspaceSwitching.value = true;
    try {
      await options.beforeSwitch();
      options.navigateToWorkspace(workspaceId);
      await options.switchWorkspace(workspaceId);
      options.afterSwitch();
      workspacePickerOpen.value = false;
    } catch (cause) {
      workspacePickerError.value = cause instanceof Error ? cause.message : 'Die Welt konnte nicht geöffnet werden.';
    } finally {
      workspaceSwitching.value = false;
    }
  }

  async function createWorkspace() {
    const name = newWorkspaceName.value.trim();
    if (!name || workspaceSwitching.value) return;
    workspacePickerError.value = '';
    workspaceSwitching.value = true;
    try {
      const created = await options.createWorkspace(name);
      newWorkspaceName.value = '';
      if (!options.workspace.value) await options.initializeWorkspace(created.id);
      else {
        workspaceSwitching.value = false;
        await switchWorkspace(created.id);
      }
    } catch (cause) {
      workspacePickerError.value = cause instanceof Error ? cause.message : 'Die Welt konnte nicht angelegt werden.';
    } finally {
      workspaceSwitching.value = false;
    }
  }

  return { workspacePickerOpen, managedWorkspace, workspaceSwitching, workspacePickerError, newWorkspaceName,
    toggleWorkspacePicker, openWorkspaceManagement, workspaceManagementChanged, restoreArchivedWorkspace, switchWorkspace, createWorkspace };
}
